import { useMemo, useEffect, useState, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, Calculator, Droplets, Activity, FlaskConical, Microscope, Store, ArrowRight, X, ExternalLink, Brain, Info } from "lucide-react";
import { motion } from "framer-motion";
import type { CompoundSearchItem, DbListing } from "@/hooks/useDatabase";
import { trackBuyClick } from "@/lib/trackClick";
import { logSearchImpression, logSearchClick } from "@/lib/trackAliasSearch";
import { normalizeAliasKey } from "@/lib/aliasNormalize";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { isBhgLabs } from "@/lib/bhgLabs";

// Per-match-type metadata used by the dev-only score breakdown badge.
// Score values mirror the ones assigned in matchedCompoundsScored below —
// keep the two in sync if you re-tune ranking.
const MATCH_TYPE_META: Record<
  "name" | "alias_exact" | "alias_prefix" | "alias_substring" | "name_prefix" | "name_substring" | "none",
  { label: string; score: number; explain: (q: string, name: string, alias: string | null) => string }
> = {
  name:            { label: "Exact name",      score: 100, explain: (q, n)    => `Query "${q}" === compound name "${n}". Highest possible rank.` },
  alias_exact:     { label: "Exact alias",     score: 95,  explain: (q, _, a) => `Query "${q}" === alias "${a}". Treated almost as strong as a name match.` },
  name_prefix:     { label: "Name prefix",     score: 80,  explain: (q, n)    => `Compound name "${n}" starts with "${q}". User likely typing the canonical name.` },
  alias_prefix:    { label: "Alias prefix",    score: 70,  explain: (q, _, a) => `Alias "${a}" starts with "${q}". Strong signal but ranked below name prefixes.` },
  name_substring:  { label: "Name substring",  score: 50,  explain: (q, n)    => `Compound name "${n}" contains "${q}" somewhere. Weaker than a prefix.` },
  alias_substring: { label: "Alias substring", score: 40,  explain: (q, _, a) => `Alias "${a}" contains "${q}" somewhere. Lowest-confidence match, easy to be a false positive.` },
  none:            { label: "No match",        score: 0,   explain: () => "" },
};

// Toggle dev mode via ?debugScore=1 OR localStorage.bhg_debug_search="1".
// Keeps the breakdown out of normal users' way while letting us flip it on
// in any session without rebuilding.
function useDebugScores(): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const fromUrl = url.searchParams.get("debugScore") === "1";
      if (fromUrl) {
        localStorage.setItem("bhg_debug_search", "1");
      }
      setOn(fromUrl || localStorage.getItem("bhg_debug_search") === "1");
    } catch {
      setOn(false);
    }
  }, []);
  return on;
}

const FORM_FILTERS = [
  { key: "all", label: "All" },
  { key: "vial", label: "💉 Vial" },
  { key: "nasal", label: "👃 Nasal" },
  { key: "oral", label: "💊 Oral" },
  { key: "topical", label: "🧴 Topical" },
];

const tools = [
  { icon: Droplets, label: "Reconstitution Calculator", href: "/tools/reconstitution" },
  { icon: FlaskConical, label: "Intranasal Calculator", href: "/tools/intranasal" },
  { icon: Activity, label: "Half-Life Visualizer", href: "/tools/halflife" },
  { icon: Microscope, label: "COA Reader", href: "/tools/coa" },
  { icon: BarChart3, label: "Price Comparison", href: "/compare" },
];

const popularSearches = ["BPC-157", "Semaglutide", "Retatrutide", "Tirzepatide", "TB-500", "CJC-1295"];

const MIN_QUERY_LENGTH = 2;

// Synonym → canonical form filter. Matched as whole words, longest first.
const FORM_SYNONYMS: Array<{ pattern: RegExp; form: string; phrase: string }> = [
  { pattern: /\bnasal\s*spray\b/i, form: "nasal", phrase: "nasal spray" },
  { pattern: /\bintra[-\s]?nasal\b/i, form: "nasal", phrase: "intranasal" },
  { pattern: /\bnose\s*spray\b/i, form: "nasal", phrase: "nose spray" },
  { pattern: /\bnasal\b/i, form: "nasal", phrase: "nasal" },
  { pattern: /\boral\s+ingestible\b/i, form: "oral", phrase: "oral ingestible" },
  { pattern: /\bingestible\b/i, form: "oral", phrase: "ingestible" },
  { pattern: /\bsublingual\b/i, form: "oral", phrase: "sublingual" },
  { pattern: /\bcapsule(s)?\b/i, form: "oral", phrase: "capsule" },
  { pattern: /\btablet(s)?\b/i, form: "oral", phrase: "tablet" },
  { pattern: /\btroche(s)?\b/i, form: "oral", phrase: "troche" },
  { pattern: /\boral\b/i, form: "oral", phrase: "oral" },
  { pattern: /\btopical\s+cream\b/i, form: "topical", phrase: "topical cream" },
  { pattern: /\btransdermal\b/i, form: "topical", phrase: "transdermal" },
  { pattern: /\bcream\b/i, form: "topical", phrase: "cream" },
  { pattern: /\bointment\b/i, form: "topical", phrase: "ointment" },
  { pattern: /\btopical\b/i, form: "topical", phrase: "topical" },
  { pattern: /\bgel\b/i, form: "topical", phrase: "gel" },
  { pattern: /\bvial(s)?\b/i, form: "vial", phrase: "vial" },
  { pattern: /\binjectable\b/i, form: "vial", phrase: "injectable" },
];

function detectFormSynonym(rawQuery: string): { form: string; phrase: string; cleanedQuery: string } | null {
  for (const { pattern, form, phrase } of FORM_SYNONYMS) {
    if (pattern.test(rawQuery)) {
      const cleaned = rawQuery.replace(pattern, " ").replace(/\s+/g, " ").trim();
      return { form, phrase, cleanedQuery: cleaned };
    }
  }
  return null;
}

/** Highlights matched substring(s) in text */
function highlightMatch(text: string, query: string): ReactNode {
  if (!query || query.length < MIN_QUERY_LENGTH) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="text-primary font-semibold bg-primary/10 rounded-sm px-0.5">{text.slice(idx, idx + query.length)}</span>
      {text.slice(idx + query.length)}
    </>
  );
}

export interface NootCompoundSearchItem {
  id: string;
  name: string;
  slug: string;
  category: string;
  effects: string[];
}

interface Props {
  query: string;
  /** The light search rows (useCompoundSearchIndex): id, slug, name, aliases and category are all this reads. */
  compounds: CompoundSearchItem[];
  vendors: { id: string; name: string; slug: string; logo: string | null; priority?: number }[];
  listings?: DbListing[];
  nootropics?: NootCompoundSearchItem[];
  onClose: () => void;
  mobile?: boolean;
}

export default function SearchResults({ query, compounds, vendors, listings = [], nootropics = [], onClose, mobile }: Props) {
  const navigate = useNavigate();
  const debugScores = useDebugScores();
  const [formFilter, setFormFilter] = useState<string>("all");
  // User can override an auto-applied filter; remember the synonym we last auto-applied
  // so we don't keep overwriting their manual choice for the same query.
  const [autoAppliedPhrase, setAutoAppliedPhrase] = useState<string | null>(null);
  const rawQ = query.toLowerCase().trim();

  // Detect form-type synonyms in the query
  const synonym = useMemo(() => detectFormSynonym(rawQ), [rawQ]);

  // The query used for actual compound/alias matching (synonym stripped if present)
  const q = synonym && synonym.cleanedQuery.length >= MIN_QUERY_LENGTH ? synonym.cleanedQuery : rawQ;

  // Auto-apply the detected form filter (only when it changes — respects manual overrides afterwards)
  useEffect(() => {
    if (synonym) {
      if (synonym.phrase !== autoAppliedPhrase) {
        setFormFilter(synonym.form);
        setAutoAppliedPhrase(synonym.phrase);
      }
    } else if (autoAppliedPhrase) {
      // Synonym was removed from the query — clear the auto-filter
      setFormFilter("all");
      setAutoAppliedPhrase(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [synonym?.phrase]);

  const matchedCompoundsScored = useMemo(() => {
    if (q.length < MIN_QUERY_LENGTH) return [];
    // Pre-compute the normalized key once per query so dash/underscore/space
    // variants ("BPC 157", "bpc-157", "BPC_157", "bpc157") all converge.
    const qKey = normalizeAliasKey(q);
    return compounds
      .map((c) => {
        const name = c.name.toLowerCase();
        const nameKey = normalizeAliasKey(c.name);
        const aliasesRaw = (c.aliases || []).map((a) => a.toLowerCase());
        // Parallel array of normalized keys, one per alias, used for the
        // alias_* match types so formatting variants are treated as equivalent.
        const aliasKeys = (c.aliases || []).map((a) => normalizeAliasKey(a));
        let score = 0;
        let matchType: "name" | "alias_exact" | "alias_prefix" | "alias_substring" | "name_prefix" | "name_substring" | "none" = "none";
        let matchedAlias: string | null = null;
        const findAliasIdx = (pred: (k: string) => boolean) => aliasKeys.findIndex((k) => k && pred(k));
        if (name === q || (qKey && nameKey === qKey)) { score = 100; matchType = "name"; }
        else if (qKey && (() => { const i = findAliasIdx((k) => k === qKey); if (i >= 0) { matchedAlias = aliasesRaw[i]; return true; } return false; })()) { score = 95; matchType = "alias_exact"; }
        else if (name.startsWith(q) || (qKey && nameKey.startsWith(qKey))) { score = 80; matchType = "name_prefix"; }
        else if (qKey && (() => { const i = findAliasIdx((k) => k.startsWith(qKey)); if (i >= 0) { matchedAlias = aliasesRaw[i]; return true; } return false; })()) { score = 70; matchType = "alias_prefix"; }
        else if (name.includes(q) || (qKey && nameKey.includes(qKey))) { score = 50; matchType = "name_substring"; }
        else if (qKey && (() => { const i = findAliasIdx((k) => k.includes(qKey)); if (i >= 0) { matchedAlias = aliasesRaw[i]; return true; } return false; })()) { score = 40; matchType = "alias_substring"; }
        return { c, score, matchType, matchedAlias };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  }, [q, compounds]);

  // Compound IDs that have an active listing matching the selected form
  const compoundIdsWithForm = useMemo(() => {
    if (formFilter === "all") return null;
    return new Set(listings.filter(l => l.active && l.form_type === formFilter).map(l => l.compound_id));
  }, [formFilter, listings]);

  const matchedCompounds = useMemo(() => {
    const base = matchedCompoundsScored.map((x) => x.c);
    if (!compoundIdsWithForm) return base;
    return base.filter((c) => compoundIdsWithForm.has(c.id));
  }, [matchedCompoundsScored, compoundIdsWithForm]);

  // Lookup of compound_id → scoring details, used by the dev-only breakdown
  // badge so the row can render its rank/match-type without a second pass.
  const scoreById = useMemo(() => {
    const m = new Map<string, { score: number; matchType: keyof typeof MATCH_TYPE_META; matchedAlias: string | null; rank: number }>();
    matchedCompoundsScored.forEach((x, i) => m.set(x.c.id, { score: x.score, matchType: x.matchType, matchedAlias: x.matchedAlias, rank: i + 1 }));
    return m;
  }, [matchedCompoundsScored]);

  const matchedTools = useMemo(() => {
    if (q.length < MIN_QUERY_LENGTH) return [];
    return tools.filter(t => t.label.toLowerCase().includes(q)).slice(0, 3);
  }, [q]);

  const matchedVendors = useMemo(() => {
    if (q.length < MIN_QUERY_LENGTH) return [];
    return vendors.filter(v => v.name.toLowerCase().includes(q)).slice(0, 3);
  }, [q, vendors]);

  const matchedNootropics = useMemo(() => {
    if (q.length < MIN_QUERY_LENGTH) return [];
    return nootropics.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.category.toLowerCase().includes(q) ||
      (c.effects || []).some(e => e.toLowerCase().includes(q))
    ).slice(0, 5);
  }, [q, nootropics]);

  const go = (href: string) => {
    onClose();
    navigate(href);
  };

  const goCompound = (slug: string) => {
    logSearchClick(query, slug);
    go(`/compound/${slug}`);
  };

  // Fire impression telemetry whenever the matched set settles
  useEffect(() => {
    if (q.length < MIN_QUERY_LENGTH) return;
    const top = matchedCompoundsScored[0];
    const totalResults =
      matchedCompoundsScored.length + matchedTools.length + matchedVendors.length + matchedNootropics.length;
    logSearchImpression({
      query,
      matchedCompoundSlug: top?.c.slug ?? null,
      matchedAlias: top?.matchedAlias ?? null,
      matchType: top?.matchType ?? "none",
      resultCount: totalResults,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, matchedCompoundsScored.length, matchedTools.length, matchedVendors.length, matchedNootropics.length]);

  const containerClass = mobile
    ? "bg-card border-t border-border/40 overflow-y-auto max-h-[70vh]"
    : "absolute left-0 right-0 top-full mt-3 bg-card border border-border rounded-2xl shadow-elevated overflow-hidden z-30";

  // Empty state — popular searches (shown for empty or too-short queries)
  if (q.length < MIN_QUERY_LENGTH) {
    return (
      <motion.div
        initial={{ opacity: 0, y: mobile ? 0 : -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: mobile ? 0 : -4 }}
        className={containerClass}
      >
        <div className="p-4">
          <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-widest mb-3">
            {q.length === 0 ? "Popular searches" : "Type at least 2 characters"}
          </p>
          <div className="flex flex-wrap gap-2">
            {popularSearches.map(s => (
              <button key={s} onClick={() => { const c = compounds.find(c => c.name === s); if (c) go(`/compound/${c.slug}`); }} className="px-3 py-2 text-sm bg-secondary text-secondary-foreground rounded-lg hover:bg-primary hover:text-primary-foreground transition-all font-medium">
                {s}
              </button>
            ))}
          </div>
        </div>
      </motion.div>
    );
  }

  const hasResults = matchedCompounds.length > 0 || matchedTools.length > 0 || matchedVendors.length > 0 || matchedNootropics.length > 0;

  // The listing a compound row's price button goes to: vendor priority first, then the lower price.
  const rowBestListing = (compoundId: string) => {
    const compoundListings = listings.filter(l => l.compound_id === compoundId && l.active);
    return compoundListings.length > 0
      ? compoundListings.reduce((best, curr) => {
          const bestPriority = vendors.find(v => v.id === best.vendor_id)?.priority ?? 999;
          const currPriority = vendors.find(v => v.id === curr.vendor_id)?.priority ?? 999;
          if (currPriority < bestPriority) return curr;
          if (currPriority === bestPriority && curr.price < best.price) return curr;
          return best;
        }, compoundListings[0])
      : null;
  };
  // Up to five rows can go to BHG Labs, so the ownership note sits once under the list and each of those rows names
  // the store.
  const anyBhgLabsRow = matchedCompounds.some((c) => {
    const l = rowBestListing(c.id);
    return !!l && isBhgLabs(vendors.find(v => v.id === l.vendor_id));
  });

  // Form-type filter chip bar (rendered above results when there are any compound matches OR a filter is active)
  const showFormBar = matchedCompoundsScored.length > 0 || formFilter !== "all";
  const formBar = showFormBar ? (
    <div className="px-3 py-2 border-b border-border/40 bg-secondary/20 space-y-1.5">
      <div className="flex items-center gap-1 flex-wrap">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mr-1">Form:</span>
        {FORM_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => {
              setFormFilter(f.key);
              // Manual override clears the auto-applied tracking so user choice sticks
              setAutoAppliedPhrase(null);
            }}
            className={`px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors ${
              formFilter === f.key
                ? "bg-primary text-primary-foreground"
                : "bg-secondary/60 text-muted-foreground hover:text-foreground"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      {synonym && autoAppliedPhrase === synonym.phrase && (
        <p className="text-[10px] text-muted-foreground">
          Detected <span className="text-foreground font-semibold">"{synonym.phrase}"</span>, filtering by{" "}
          <span className="text-primary font-semibold capitalize">{synonym.form}</span>
          {synonym.cleanedQuery && synonym.cleanedQuery !== rawQ && (
            <> · searching <span className="text-foreground font-semibold">"{synonym.cleanedQuery}"</span></>
          )}
        </p>
      )}
    </div>
  ) : null;

  if (!hasResults) {
    return (
      <motion.div
        initial={{ opacity: 0, y: mobile ? 0 : -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: mobile ? 0 : -4 }}
        className={containerClass}
      >
        {formBar}
        <div className="p-5 text-center">
          <p className="text-sm text-muted-foreground">
            {formFilter !== "all" && matchedCompoundsScored.length > 0
              ? `No ${FORM_FILTERS.find(f => f.key === formFilter)?.label.replace(/^[^ ]+ /, "").toLowerCase()} listings for "${query}"`
              : `No results for "${query}"`}
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: mobile ? 0 : -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: mobile ? 0 : -4 }}
      className={containerClass}
    >
      {formBar}
      {/* Compounds — with aliases */}
      {matchedCompounds.length > 0 && (
        <div>
          <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-widest px-4 pt-4 pb-2">Compounds</p>
          {matchedCompounds.map((c) => {
            const aliases = c.aliases || [];
            // Find best listing for this compound, prioritizing by vendor priority (lower = better)
            const bestListing = rowBestListing(c.id);
            const bestVendor = bestListing ? vendors.find(v => v.id === bestListing.vendor_id) : null;
            return (
              <div key={c.id} className="flex items-center w-full hover:bg-secondary/50 transition-colors group">
                <button onClick={() => goCompound(c.slug)} className="flex-1 text-left px-4 py-3 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="font-semibold text-foreground group-hover:text-primary transition-colors">
                      {highlightMatch(c.name, q)}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] bg-secondary text-secondary-foreground px-2 py-0.5 rounded font-medium">{c.category}</span>
                      {debugScores && scoreById.get(c.id) && (
                        <ScoreBreakdownBadge
                          query={q}
                          name={c.name}
                          info={scoreById.get(c.id)!}
                        />
                      )}
                      {!bestListing && (
                        <span className="text-[10px] text-primary font-semibold flex items-center gap-0.5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                          Compare <ArrowRight className="h-2.5 w-2.5" />
                        </span>
                      )}
                    </div>
                  </div>
                  {aliases.length > 0 && (
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      <span className="text-muted-foreground/60">Also known as: </span>
                      {aliases.map((a, i) => (
                        <span key={a}>
                          {highlightMatch(a, q)}
                          {i < aliases.length - 1 ? ", " : ""}
                        </span>
                      ))}
                    </p>
                  )}
                  {bestListing && isBhgLabs(bestVendor) && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">The price button goes to BHG Labs.</p>
                  )}
                </button>
                {bestListing && (
                  <a
                    href={bestListing.affiliate_url || "#"}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    onClick={(e) => {
                      e.stopPropagation();
                      trackBuyClick({ compoundId: c.id, vendorId: bestListing.vendor_id, listingId: bestListing.id, compoundName: c.name, vendorName: bestVendor?.name, source: "search-results" });
                    }}
                    className="shrink-0 mr-3 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-[11px] font-semibold hover:opacity-90 transition-opacity flex items-center gap-1"
                  >
                    ${bestListing.price.toFixed(0)} <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Tools */}
      {matchedTools.length > 0 && (
        <div className="border-t border-border/40">
          <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-widest px-4 pt-3 pb-1.5">Tools</p>
          {matchedTools.map(t => (
            <button key={t.href} onClick={() => go(t.href)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-secondary/50 transition-colors text-left">
              <t.icon className="h-4 w-4 text-primary shrink-0" />
              <span className="text-sm font-medium text-foreground">{highlightMatch(t.label, q)}</span>
              <ArrowRight className="h-3 w-3 text-muted-foreground/40 ml-auto" />
            </button>
          ))}
        </div>
      )}

      {/* Nootropics */}
      {matchedNootropics.length > 0 && (
        <div className="border-t border-border/40">
          <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-widest px-4 pt-3 pb-1.5">Nootropics</p>
          {matchedNootropics.map(c => (
            <button key={c.id} onClick={() => go(`/nootropic/${c.slug}`)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-secondary/50 transition-colors text-left group">
              <Brain className="h-4 w-4 text-primary shrink-0" />
              <div className="min-w-0 flex-1">
                <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{highlightMatch(c.name, q)}</span>
                <span className="text-[10px] bg-secondary text-secondary-foreground px-2 py-0.5 rounded font-medium ml-2">{c.category}</span>
              </div>
              <ArrowRight className="h-3 w-3 text-muted-foreground/40 ml-auto" />
            </button>
          ))}
        </div>
      )}

      {/* Vendors */}
      {matchedVendors.length > 0 && (
        <div className="border-t border-border/40">
          <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-widest px-4 pt-3 pb-1.5">Vendors</p>
          {matchedVendors.map(v => (
            <button key={v.id} onClick={() => go(`/vendors/${v.slug}`)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-secondary/50 transition-colors text-left">
              <Store className="h-4 w-4 text-primary shrink-0" />
              <span className="text-sm font-medium text-foreground">{highlightMatch(v.name, q)}</span>
              <ArrowRight className="h-3 w-3 text-muted-foreground/40 ml-auto" />
            </button>
          ))}
        </div>
      )}

      {/* Keyboard hint — desktop only */}
      {!mobile && (
        <div className="border-t border-border/40 px-4 py-2.5 flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">
            <kbd className="px-1.5 py-0.5 bg-secondary rounded text-[10px] font-mono">Enter</kbd> compare top result
          </span>
          <span className="text-[10px] text-muted-foreground">
            <kbd className="px-1.5 py-0.5 bg-secondary rounded text-[10px] font-mono">Esc</kbd> close
          </span>
        </div>
      )}
    </motion.div>
  );
}

/**
 * Dev-only ranking badge. Shows the compound's rank, total score, and a
 * tooltip explaining which match-type fired (e.g., "exact alias 95 vs.
 * substring 40") so we can debug why one compound outranks another for a
 * given query without sprinkling console.logs.
 *
 * Toggle via ?debugScore=1 in the URL or localStorage.bhg_debug_search="1".
 */
function ScoreBreakdownBadge({
  query,
  name,
  info,
}: {
  query: string;
  name: string;
  info: { score: number; matchType: keyof typeof MATCH_TYPE_META; matchedAlias: string | null; rank: number };
}) {
  const meta = MATCH_TYPE_META[info.matchType];
  // Build a comparison list of OTHER possible match types so the tooltip
  // shows "this match scored 95, but a substring would only have scored 40".
  const ladder = (Object.keys(MATCH_TYPE_META) as Array<keyof typeof MATCH_TYPE_META>)
    .filter((k) => k !== "none")
    .sort((a, b) => MATCH_TYPE_META[b].score - MATCH_TYPE_META[a].score);
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border border-primary/30 bg-primary/10 text-primary cursor-help"
          >
            <Info className="h-2.5 w-2.5" />
            #{info.rank} · {info.score}
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-2 text-xs">
            <div>
              <p className="font-semibold text-foreground">
                {meta.label} <span className="text-primary">({info.score})</span>
              </p>
              <p className="text-muted-foreground mt-0.5 leading-snug">
                {meta.explain(query, name, info.matchedAlias)}
              </p>
            </div>
            <div className="border-t border-border/40 pt-1.5">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Score ladder</p>
              <ul className="space-y-0.5">
                {ladder.map((k) => (
                  <li
                    key={k}
                    className={`flex items-center justify-between gap-3 ${
                      k === info.matchType ? "text-primary font-semibold" : "text-muted-foreground"
                    }`}
                  >
                    <span>{MATCH_TYPE_META[k].label}</span>
                    <span className="font-mono">{MATCH_TYPE_META[k].score}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}